import { useState, useCallback } from 'react';
import { productPartService } from '../services/api';
import { ProductPart } from '../types/mold';
import { AxiosError } from 'axios';
import { getErrorMessage } from '../utils/apiError';

export interface UpdateProductPartData {
  partNumber?: string;
  partName?: string;
  customerId?: number;
  history?: string;
  model?: string;
  description?: string;
  status?: string;
  isActive?: boolean;
};

export const useProductParts = () => {
  const [productParts, setProductParts] = useState<ProductPart[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProductParts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productPartService.getProductParts();
      setProductParts(response.data);
    } catch (err) {
      setError('Failed to fetch product parts');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProductParts = async (id: number, data: UpdateProductPartData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await productPartService.updateProductPart(id, data);
      console.log('Update result:', result);
      await fetchProductParts(); // Refresh list after update
    } catch (err) {
      const errorMessage = getErrorMessage(err as AxiosError);
      setError(errorMessage || 'Failed to update product part');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createProductPart = async (data: {
    partNumber: string;
    partName: string;
    customerId: number;
    history?: string;
    model?: string;
    description?: string;
    status?: string;
    isActive?: boolean;
  }): Promise<ProductPart> => {
    try {
      setLoading(true);
      setError(null);
      const response = await productPartService.createProductPart(data);
      await fetchProductParts(); // Refresh list after creation
      return response.data; // Return the created part
    } catch (err) {
      setError('Failed to create product part');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { 
    productParts, 
    loading, 
    error, 
    retry: fetchProductParts, 
    fetchProductParts, 
    updateProductParts,
    createProductPart 
  };
};
